import { Capacitor } from '@capacitor/core'
import { $fetch, type FetchOptions } from 'ofetch'

function normalizeApiBase(apiBase?: string) {
  return (apiBase ? apiBase.replace(/\/$/, '') : '') || '/api'
}

function normalizeMwPath(path: string) {
  if (!path) {
    return ''
  }

  return path.startsWith('/') ? path : `/${path}`
}

export function useMwBff() {
  const config = useRuntimeConfig()
  const isCapacitor = Capacitor.isNativePlatform()
  const apiBase = normalizeApiBase(config.public.apiBase)

  /**
   * A route served by THIS app's Nitro server — the API proxy under
   * `/api/mw/*`, and the console's own endpoints (`/api/messenger/*`,
   * `/api/support/*`, `/api/ai/*`).
   *
   * On the web the path is relative: the browser reaches the origin that served
   * the page, which is the Nitro server. The native app has no such origin. Its
   * WebView is served from the device, and Capacitor's local server answers any
   * extension-less path it does not hold with `index.html`, status 200 — so a
   * relative `$fetch` there never fails; it resolves with the app's own HTML as
   * a string, truthy and nothing like the JSON the caller typed it as, and the
   * first property read on it throws inside a computed or a template. That is
   * how `useMessenger` and `OfferBanner` reached Sentry (2026-09). Every Nitro
   * route is therefore addressed at `bffUrl` on native, as `/api/mw/*` always
   * was; `buildFetchOptions` adds the session bearer beside it.
   */
  function resolveNitroUrl(path: string) {
    const nitroPath = normalizeMwPath(path)

    if (!isCapacitor) {
      return nitroPath
    }

    const bffBaseUrl = (config.public.bffUrl || '').replace(/\/$/, '')
    return bffBaseUrl ? `${bffBaseUrl}${nitroPath}` : nitroPath
  }

  function resolveBffUrl(path = '') {
    return resolveNitroUrl(`${apiBase}/mw${normalizeMwPath(path)}`)
  }

  async function buildFetchOptions<T>(options: FetchOptions<'json'> = {}): Promise<FetchOptions<'json'>> {
    const headers = {
      ...((options.headers as Record<string, string> | undefined) ?? {}),
    }

    if (isCapacitor) {
      const { getSessionId } = useBffSession()
      const sessionId = await getSessionId()

      // Login, signup, account selection and the public catalog precede a
      // session. The BFF owns which routes require authentication.
      if (sessionId) headers.Authorization = `Bearer ${sessionId}`
    }

    return {
      ...options,
      headers,
      credentials: isCapacitor ? 'omit' : (options.credentials ?? 'include'),
    }
  }

  /** `path` is relative to the API proxy: `bffFetch('/whmcs/me')` → `/api/mw/whmcs/me`. */
  async function bffFetch<T>(path: string, options: FetchOptions<'json'> = {}) {
    return await $fetch<T>(resolveBffUrl(path), await buildFetchOptions(options))
  }

  /** `path` is the whole Nitro route: `nitroFetch('/api/messenger/capabilities')`. */
  async function nitroFetch<T>(path: string, options: FetchOptions<'json'> = {}) {
    return await $fetch<T>(resolveNitroUrl(path), await buildFetchOptions(options))
  }

  return {
    isCapacitor,
    resolveBffUrl,
    resolveNitroUrl,
    buildFetchOptions,
    bffFetch,
    nitroFetch,
  }
}
